import type { ErrorResponse } from '@nest-react-monorepo/contracts';

/**
 * Rule E8: a request whose deadline elapsed before any response arrived. The
 * command may already have completed on the server - the client only knows
 * it stopped waiting. Deliberately not an `ApiRequestError`: no server
 * payload was ever received, so there is no status, code or requestId to
 * carry. That absence is also what makes `lib/financial-request.ts`'s
 * `isUncertain()` treat this the same as a network failure or a 5xx: any
 * error that is not an `ApiRequestError` is already unresolved rather than
 * rejected, so a timeout keeps `useCommand`'s pending intent (`features/
 * currency/data.ts`) recoverable instead of discarding it as a confirmed
 * failure (rule E17).
 */
/**
 * The synthetic id `api-client` falls back to when a response carries no
 * `x-request-id`. Rule E15: a correlation reference is shown only when the
 * server actually issued one — a fabricated id sends a user to support with
 * something nobody can look up.
 */
export const UNKNOWN_REQUEST_ID = 'unknown';

export class ApiTimeoutError extends Error {
  constructor() {
    super('Request timed out; the outcome is unknown.');
    this.name = 'ApiTimeoutError';
  }
}

/** A typed non-success response returned by the API client. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly payload: ErrorResponse;

  constructor(status: number, payload: ErrorResponse) {
    super(payload.error.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.payload = payload;
  }

  get code(): ErrorResponse['error']['code'] {
    return this.payload.error.code;
  }

  get requestId(): string {
    return this.payload.requestId;
  }

  get fieldErrors(): NonNullable<ErrorResponse['error']['fieldErrors']> {
    return this.payload.error.fieldErrors ?? [];
  }
}
